import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { LogOut, RefreshCw, Mail, Inbox, CheckCheck, Trash2, X, ExternalLink, FileText, Phone } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { formatXPF, FORMULES } from "@/lib/data";

const STATUT_LABELS = {
  nouveau: { label: "Nouveau", color: "bg-coral/15 text-coral" },
  contacte: { label: "Contacté", color: "bg-lagoon/30 text-ocean" },
  confirme: { label: "Confirmé", color: "bg-green-100 text-green-700" },
  annule: { label: "Annulé", color: "bg-red-100 text-red-600" },
};

const PAIEMENT_LABELS = {
  en_attente: { label: "En attente", color: "bg-yellow-100 text-yellow-700" },
  paye: { label: "Payé", color: "bg-green-100 text-green-700" },
  rembourse: { label: "Remboursé", color: "bg-gray-100 text-gray-700" },
};

const formuleLabel = (id) => FORMULES.find((f) => f.id === id)?.label || id;

export default function AdminDashboardPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("inscriptions");
  const [inscriptions, setInscriptions] = useState([]);
  const [messages, setMessages] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterStatut, setFilterStatut] = useState("");
  const [filterPaiement, setFilterPaiement] = useState("");
  const [selected, setSelected] = useState(null);
  const [selectedMsg, setSelectedMsg] = useState(null);

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [insRes, msgRes, statsRes] = await Promise.all([
        api.get("/admin/inscriptions", { params: { statut: filterStatut || undefined, paiement: filterPaiement || undefined } }),
        api.get("/admin/messages"),
        api.get("/admin/stats"),
      ]);
      setInscriptions(insRes.data || []);
      setMessages(msgRes.data || []);
      setStats(statsRes.data);
    } catch (e) {
      toast.error("Erreur de chargement");
    } finally {
      setLoading(false);
    }
  }, [filterStatut, filterPaiement]);

  useEffect(() => { load(); }, [load]);

  const updateInscription = async (id, updates) => {
    try {
      const { data } = await api.patch(`/admin/inscriptions/${id}`, updates);
      setInscriptions((arr) => arr.map((i) => (i.id === id ? data : i)));
      if (selected?.id === id) setSelected(data);
      toast.success("Mise à jour effectuée");
      load();
    } catch {
      toast.error("Erreur de mise à jour");
    }
  };

  const deleteInscription = async (id) => {
    if (!window.confirm("Supprimer définitivement cette inscription ?")) return;
    try {
      await api.delete(`/admin/inscriptions/${id}`);
      setInscriptions((arr) => arr.filter((i) => i.id !== id));
      setSelected(null);
      toast.success("Inscription supprimée");
      load();
    } catch {
      toast.error("Erreur de suppression");
    }
  };

  const markMessageRead = async (id) => {
    try {
      await api.patch(`/admin/messages/${id}/read`);
      setMessages((arr) => arr.map((m) => (m.id === id ? { ...m, lu: true } : m)));
      load();
    } catch (err) {
      console.error("Failed to mark message as read:", err);
    }
  };

  const deleteMessage = async (id) => {
    if (!window.confirm("Supprimer ce message ?")) return;
    try {
      await api.delete(`/admin/messages/${id}`);
      setMessages((arr) => arr.filter((m) => m.id !== id));
      setSelectedMsg(null);
      load();
    } catch {
      toast.error("Erreur");
    }
  };

  return (
    <div className="min-h-screen bg-sand">
      {/* Top bar */}
      <header className="sticky top-0 z-30 glass border-b border-ocean/5">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-white border border-ocean/10 flex items-center justify-center p-1.5">
              <img src="/logo-ssp.png" alt="SSP" className="w-full h-full object-contain" />
            </div>
            <div>
              <div className="font-display text-base text-ocean">Tableau de bord</div>
              <div className="text-[11px] uppercase tracking-widest text-coral font-semibold">Soutien Scolaire Papara</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden sm:inline text-sm text-ocean/55">{user?.email}</span>
            <Link to="/" className="hidden sm:inline-flex items-center gap-1 text-sm text-ocean/55 hover:text-ocean" data-testid="link-go-public">
              <ExternalLink size={14} /> Site public
            </Link>
            <button onClick={handleLogout} data-testid="btn-logout" className="btn-pill btn-ghost text-sm">
              <LogOut size={16} /> Déconnexion
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
          <StatCard label="Total inscriptions" value={stats?.total_inscriptions ?? 0} accent />
          <StatCard label="Nouveaux" value={stats?.nouveaux ?? 0} />
          <StatCard label="Confirmés" value={stats?.confirmes ?? 0} />
          <StatCard label="Payés" value={stats?.payes ?? 0} />
          <StatCard label="Messages non lus" value={stats?.messages_non_lus ?? 0} />
        </div>

        {/* Tabs */}
        <div className="flex items-center justify-between mb-5 gap-3 flex-wrap">
          <div className="inline-flex bg-white rounded-full p-1 border border-ocean/5">
            <TabBtn active={tab === "inscriptions"} onClick={() => setTab("inscriptions")} testid="tab-inscriptions">
              <Inbox size={15} /> Inscriptions
              <span className="text-[10px] bg-coral/15 text-coral px-1.5 py-0.5 rounded-full ml-1">{inscriptions.length}</span>
            </TabBtn>
            <TabBtn active={tab === "messages"} onClick={() => setTab("messages")} testid="tab-messages">
              <Mail size={15} /> Messages
              <span className="text-[10px] bg-coral/15 text-coral px-1.5 py-0.5 rounded-full ml-1">{messages.length}</span>
            </TabBtn>
          </div>

          {tab === "inscriptions" && (
            <div className="flex items-center gap-2 flex-wrap">
              <select value={filterStatut} onChange={(e) => setFilterStatut(e.target.value)} data-testid="filter-statut" className="px-3 py-2 rounded-full text-sm border border-ocean/10 bg-white text-ocean">
                <option value="">Tous statuts</option>
                <option value="nouveau">Nouveau</option>
                <option value="contacte">Contacté</option>
                <option value="confirme">Confirmé</option>
                <option value="annule">Annulé</option>
              </select>
              <select value={filterPaiement} onChange={(e) => setFilterPaiement(e.target.value)} data-testid="filter-paiement" className="px-3 py-2 rounded-full text-sm border border-ocean/10 bg-white text-ocean">
                <option value="">Tous paiements</option>
                <option value="en_attente">En attente</option>
                <option value="paye">Payé</option>
                <option value="rembourse">Remboursé</option>
              </select>
              <button onClick={load} data-testid="btn-refresh" className="btn-pill btn-ghost text-sm">
                <RefreshCw size={14} /> Actualiser
              </button>
            </div>
          )}
        </div>

        {/* Content */}
        {tab === "inscriptions" ? (
          <div className="bg-white rounded-3xl border border-ocean/5 overflow-hidden" data-testid="inscriptions-list">
            {loading ? (
              <div className="p-10 text-center text-ocean/40">Chargement…</div>
            ) : inscriptions.length === 0 ? (
              <div className="p-10 text-center text-ocean/40">Aucune inscription pour l'instant.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead className="bg-sand/60 text-left text-xs uppercase tracking-widest text-ocean/55">
                    <tr>
                      <th className="px-5 py-4">Date</th>
                      <th className="px-5 py-4">Enfant</th>
                      <th className="px-5 py-4">Parent</th>
                      <th className="px-5 py-4">Formule</th>
                      <th className="px-5 py-4">Montant</th>
                      <th className="px-5 py-4">Statut</th>
                      <th className="px-5 py-4">Paiement</th>
                      <th className="px-5 py-4"></th>
                    </tr>
                  </thead>
                  <tbody className="text-sm">
                    {inscriptions.map((i) => (
                      <tr key={i.id} className="border-t border-ocean/5 hover:bg-sand/40 cursor-pointer" onClick={() => setSelected(i)} data-testid={`row-inscription-${i.id}`}>
                        <td className="px-5 py-4 text-ocean/65">{new Date(i.created_at).toLocaleDateString("fr-FR")}</td>
                        <td className="px-5 py-4 font-medium text-ocean">{i.enfant.prenom} {i.enfant.nom} <span className="text-ocean/40 text-xs">({i.enfant.classe})</span></td>
                        <td className="px-5 py-4 text-ocean/75">{i.parent.prenom} {i.parent.nom}</td>
                        <td className="px-5 py-4 text-ocean/75">{formuleLabel(i.formule.type_formule)}</td>
                        <td className="px-5 py-4 font-display text-ocean">{formatXPF(i.montant_estime || 0)}</td>
                        <td className="px-5 py-4"><Badge {...STATUT_LABELS[i.statut]} /></td>
                        <td className="px-5 py-4"><Badge {...PAIEMENT_LABELS[i.paiement_statut]} /></td>
                        <td className="px-5 py-4 text-coral text-xs font-medium">Voir →</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        ) : (
          <div className="bg-white rounded-3xl border border-ocean/5 overflow-hidden" data-testid="messages-list">
            {loading ? (
              <div className="p-10 text-center text-ocean/40">Chargement…</div>
            ) : messages.length === 0 ? (
              <div className="p-10 text-center text-ocean/40">Aucun message.</div>
            ) : (
              <ul>
                {messages.map((m) => (
                  <li key={m.id} className="border-b border-ocean/5 last:border-0 px-5 py-4 hover:bg-sand/40 cursor-pointer flex items-start gap-4" onClick={() => { setSelectedMsg(m); if (!m.lu) markMessageRead(m.id); }} data-testid={`row-message-${m.id}`}>
                    <div className={`w-2 h-2 rounded-full mt-2 ${m.lu ? "bg-transparent" : "bg-coral"}`}></div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-3">
                        <div className={`font-medium ${m.lu ? "text-ocean/70" : "text-ocean"}`}>{m.nom}</div>
                        <div className="text-xs text-ocean/40">{new Date(m.created_at).toLocaleDateString("fr-FR")}</div>
                      </div>
                      <div className="text-sm text-ocean/55 mt-0.5">{m.sujet}</div>
                      <div className="text-sm text-ocean/45 truncate mt-1">{m.message}</div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </main>

      {/* Drawer inscription */}
      {selected && (
        <Drawer onClose={() => setSelected(null)}>
          <div className="flex items-center justify-between mb-6">
            <h3 className="font-display text-2xl text-ocean">Inscription</h3>
            <button onClick={() => setSelected(null)} className="w-9 h-9 rounded-full bg-ocean/5 flex items-center justify-center" data-testid="close-drawer"><X size={18} /></button>
          </div>

          <div className="space-y-5">
            <Section title="Parent">
              <Row label="Nom" value={`${selected.parent.prenom} ${selected.parent.nom}`} />
              <Row label="Email" value={<a href={`mailto:${selected.parent.email}`} className="text-coral">{selected.parent.email}</a>} />
              <Row label="Téléphone" value={<a href={`tel:${selected.parent.telephone}`} className="text-coral">{selected.parent.telephone}</a>} />
              {selected.parent.adresse && <Row label="Adresse" value={selected.parent.adresse} />}
            </Section>

            <Section title="Enfant">
              <Row label="Nom" value={`${selected.enfant.prenom} ${selected.enfant.nom}`} />
              <Row label="Classe" value={selected.enfant.classe} />
              <Row label="École" value={selected.enfant.ecole} />
              {selected.enfant.date_naissance && <Row label="Date de naissance" value={selected.enfant.date_naissance} />}
              {selected.enfant.allergies && <Row label="Allergies" value={selected.enfant.allergies} />}
            </Section>

            <Section title="Formule">
              <Row label="Type" value={formuleLabel(selected.formule.type_formule)} />
              <Row label="Option" value={selected.formule.frequence} />
              {selected.formule.transport && <Row label="Transport" value={`Oui — ${selected.formule.transport_frequence}`} />}
              {selected.formule.date_debut && <Row label="Début" value={selected.formule.date_debut} />}
              <Row label="Montant estimé" value={<span className="font-display text-xl text-coral">{formatXPF(selected.montant_estime || 0)}</span>} />
            </Section>

            {selected.commentaire && (
              <Section title="Commentaire parent">
                <p className="text-sm text-ocean/70 whitespace-pre-line">{selected.commentaire}</p>
              </Section>
            )}

            <Section title="Statut">
              <div className="space-y-3">
                <div>
                  <label className="text-xs uppercase tracking-widest text-ocean/55 block mb-1">Statut</label>
                  <select value={selected.statut} onChange={(e) => updateInscription(selected.id, { statut: e.target.value })} data-testid="update-statut" className="w-full px-3 py-2.5 rounded-xl border border-ocean/10 bg-white text-sm">
                    <option value="nouveau">Nouveau</option>
                    <option value="contacte">Contacté</option>
                    <option value="confirme">Confirmé</option>
                    <option value="annule">Annulé</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-ocean/55 block mb-1">Paiement</label>
                  <select value={selected.paiement_statut} onChange={(e) => updateInscription(selected.id, { paiement_statut: e.target.value })} data-testid="update-paiement" className="w-full px-3 py-2.5 rounded-xl border border-ocean/10 bg-white text-sm">
                    <option value="en_attente">En attente</option>
                    <option value="paye">Payé</option>
                    <option value="rembourse">Remboursé</option>
                  </select>
                </div>
              </div>
            </Section>

            <div className="flex gap-2 pt-4 border-t border-ocean/5">
              <a href={`mailto:${selected.parent.email}`} className="btn-pill btn-primary text-sm flex-1 justify-center" data-testid="action-email">
                <Mail size={14} /> Email parent
              </a>
              <a href={`tel:${selected.parent.telephone}`} className="btn-pill btn-ghost text-sm flex-1 justify-center" data-testid="action-call">
                <Phone size={14} /> Appeler
              </a>
              <button onClick={() => deleteInscription(selected.id)} className="btn-pill text-sm bg-red-50 text-red-600 hover:bg-red-100 border border-red-100" data-testid="action-delete">
                <Trash2 size={14} />
              </button>
            </div>
          </div>
        </Drawer>
      )}

      {/* Drawer message */}
      {selectedMsg && (
        <Drawer onClose={() => setSelectedMsg(null)}>
          <div className="flex items-center justify-between mb-6">
            <h3 className="font-display text-2xl text-ocean">Message</h3>
            <button onClick={() => setSelectedMsg(null)} className="w-9 h-9 rounded-full bg-ocean/5 flex items-center justify-center"><X size={18} /></button>
          </div>
          <div className="space-y-4">
            <Row label="De" value={selectedMsg.nom} />
            <Row label="Email" value={<a href={`mailto:${selectedMsg.email}`} className="text-coral">{selectedMsg.email}</a>} />
            {selectedMsg.telephone && <Row label="Téléphone" value={selectedMsg.telephone} />}
            <Row label="Sujet" value={selectedMsg.sujet} />
            <Row label="Date" value={new Date(selectedMsg.created_at).toLocaleString("fr-FR")} />
            <div>
              <div className="text-xs uppercase tracking-widest text-ocean/55 mb-2">Message</div>
              <div className="bg-sand rounded-2xl p-4 text-sm text-ocean/80 whitespace-pre-line">{selectedMsg.message}</div>
            </div>
            <div className="flex gap-2 pt-4 border-t border-ocean/5">
              <a href={`mailto:${selectedMsg.email}?subject=Re: ${encodeURIComponent(selectedMsg.sujet)}`} className="btn-pill btn-primary text-sm flex-1 justify-center">
                <Mail size={14} /> Répondre
              </a>
              <button onClick={() => deleteMessage(selectedMsg.id)} className="btn-pill text-sm bg-red-50 text-red-600 hover:bg-red-100 border border-red-100">
                <Trash2 size={14} />
              </button>
            </div>
          </div>
        </Drawer>
      )}
    </div>
  );
}

function StatCard({ label, value, accent }) {
  return (
    <div className={`rounded-2xl p-5 border ${accent ? "bg-ocean text-white border-ocean" : "bg-white border-ocean/5"}`}>
      <div className={`text-xs uppercase tracking-widest font-semibold mb-1 ${accent ? "text-coral" : "text-coral"}`}>{label}</div>
      <div className={`font-display text-3xl ${accent ? "text-white" : "text-ocean"}`}>{value}</div>
    </div>
  );
}

function TabBtn({ active, onClick, children, testid }) {
  return (
    <button onClick={onClick} data-testid={testid} className={`px-4 py-2 rounded-full text-sm font-medium inline-flex items-center gap-1.5 transition-colors ${active ? "bg-ocean text-white" : "text-ocean/65 hover:text-ocean"}`}>
      {children}
    </button>
  );
}

function Badge({ label, color }) {
  return <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${color}`}>{label}</span>;
}

function Section({ title, children }) {
  return (
    <div className="bg-sand rounded-2xl p-4">
      <div className="text-xs uppercase tracking-widest text-coral font-semibold mb-3">{title}</div>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between gap-3 text-sm">
      <span className="text-ocean/55">{label}</span>
      <span className="font-medium text-ocean text-right break-words max-w-[60%]">{value}</span>
    </div>
  );
}

function Drawer({ children, onClose }) {
  return (
    <div className="fixed inset-0 z-50 flex" data-testid="drawer">
      <div className="flex-1 bg-ocean/50 backdrop-blur-sm" onClick={onClose} />
      <div className="w-full max-w-lg h-full bg-white p-6 sm:p-8 overflow-y-auto shadow-2xl">
        {children}
      </div>
    </div>
  );
}
